import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
    primaryText,
    secondaryText,
    greyTextColor,
    darkGreenTextColor,
    borderPrimary,
    placeholderPrimary,
} from "../../../../../../styling/stylingHelper";
import { calculateUnitPrice } from "../../../../../orderhistory/aggregations";
import { formatPlatformName } from "../../../../../../services/helperService";
import { eur } from "../../../../../orderhistory/utils";
import { Platform } from "../../../../../../types/Platforms";
import { platformsWithoutArticleId } from "../../../../../../utils/platformUtils";
import { MultiSelectFilter } from "../../../../../ui/MultiSelectFilter";
import { SupplierLogo } from "../../../../../ui/SupplierLogo";
import { ChevronDown, ChevronUp, Plus, Utensils } from "lucide-react";
import SourceRestaurantFilter from "./SourceRestaurantFilter";
import SourceRestaurantBadge from "../SourceRestaurantBadge";
import { useRestaurantStore } from "../../../../../../stores/appRestaurantStore";
import { PRACTICE_ID_PREFIX, isPracticeId } from "../../../../../onboarding/practice/practiceGuards";
import { scopedAnchor, usePracticeScope } from "../../../../../onboarding/practice/practiceScope";

interface FromOrdersTabProps {
    products: any[];
    truncatedCount: number;
    selectedProduct: any;
    onSelectProduct: (product: any) => void;
    quantityInputValue: string;
    quantityNeeded: number;
    onQuantityChange: (value: string, numericValue: number) => void;
    calculateEstimatedPrice: () => number | null;
    getProductPlatformDisplay: (platform: string, customPlatformName?: string) => { showLogo: boolean; displayName: string; platformForLogo?: Platform };
    searchQuery: string;
    showExVatOrders: boolean;
    // Filter props
    supplierFilters: string[];
    onSupplierFiltersChange: (filters: string[]) => void;
    availableSuppliers: string[];
    categoryFilters: string[];
    onCategoryFiltersChange: (filters: string[]) => void;
    availableCategories: string[];
    currentRestaurantId: string;
    automationReview?: boolean;
    hideSourceFilter?: boolean;
    hideFilters?: boolean;
    onCreateCustom?: (name: string) => void;
    // Guided tour only: distinguishes the picker nested inside the create-subrecipe
    // form from the one on the dish, so both can carry their own anchors at once.
    onboardingScope?: "subrecipe";
}

const FromOrdersTab: React.FC<FromOrdersTabProps> = ({
    products,
    truncatedCount,
    selectedProduct,
    onSelectProduct,
    quantityInputValue,
    quantityNeeded,
    onQuantityChange,
    calculateEstimatedPrice,
    getProductPlatformDisplay,
    searchQuery,
    showExVatOrders,
    // Filter props
    supplierFilters,
    onSupplierFiltersChange,
    availableSuppliers,
    categoryFilters,
    onCategoryFiltersChange,
    availableCategories,
    currentRestaurantId,
    automationReview = false,
    hideSourceFilter = false,
    hideFilters = false,
    onCreateCustom,
    onboardingScope,
}) => {
    const { t } = useTranslation();
    const practiceScope = usePracticeScope();
    const restaurantCount = useRestaurantStore(s => s.restaurants.length);
    const [supplierDropdownOpen, setSupplierDropdownOpen] = useState(false);
    const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
    const supplierDropdownRef = useRef<HTMLDivElement>(null);
    const categoryDropdownRef = useRef<HTMLDivElement>(null);

    const hasActiveFilters = supplierFilters.length > 0 || categoryFilters.length > 0;

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (supplierDropdownRef.current && !supplierDropdownRef.current.contains(event.target as Node)) {
                setSupplierDropdownOpen(false);
            }
            if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target as Node)) {
                setCategoryDropdownOpen(false);
            }
        };

        if (supplierDropdownOpen || categoryDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [supplierDropdownOpen, categoryDropdownOpen]);

    // Format supplier name for display
    const formatSupplierName = (supplier: string) => {
        return supplier.charAt(0).toUpperCase() + supplier.slice(1);
    };

    return (
        <div className="flex flex-1 flex-col min-h-0 gap-3">
            {/* Filters — source restaurant + supplier + category share one row */}
            {!hideFilters && (availableSuppliers.length > 0 || availableCategories.length > 0 || restaurantCount > 1) && (
                <div className="relative flex flex-wrap items-center gap-2 pb-3 border-b border-borderGrey dark:border-borderGrey-dark">
                    {/* Source restaurant selector (shared across the 3 data tabs) */}
                    {!hideSourceFilter && <SourceRestaurantFilter currentRestaurantId={currentRestaurantId} inline />}

                    {/* Supplier Filter Dropdown */}
                    {availableSuppliers.length > 0 && (
                        <div className="relative" ref={supplierDropdownRef}>
                            <button
                                type="button"
                                onClick={() => {
                                    setSupplierDropdownOpen(!supplierDropdownOpen);
                                    setCategoryDropdownOpen(false);
                                }}
                                className={`flex items-center gap-2 px-3 h-9 rounded-lg border text-[14px] font-medium transition-colors ${
                                    supplierFilters.length > 0
                                        ? 'border-darkGreenColor dark:border-darkGreenColor-dark bg-green-50 dark:bg-green-900/20 text-darkGreenColor dark:text-darkGreenColor-dark'
                                        : 'border-borderGrey dark:border-borderGrey-dark bg-backgroundSecondary dark:bg-backgroundSecondary-dark text-textSecondary dark:text-textSecondary-dark'
                                }`}
                            >
                                <span>Supplier</span>
                                {supplierFilters.length > 0 && (
                                    <span className="bg-darkGreenColor dark:bg-darkGreenColor-dark text-white dark:text-white text-[12px] px-1.5 rounded-full">
                                        {supplierFilters.length}
                                    </span>
                                )}
                                {supplierDropdownOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </button>
                            {supplierDropdownOpen && (
                                <div className="absolute top-full left-0 mt-1 z-20 bg-white dark:bg-background-dark border border-borderGrey dark:border-borderGrey-dark rounded-lg shadow-lg p-3 min-w-[280px]">
                                    <MultiSelectFilter
                                        label="Suppliers"
                                        selectedValues={supplierFilters}
                                        onValuesChange={onSupplierFiltersChange}
                                        availableOptions={availableSuppliers.map(s => ({ id: s, name: formatSupplierName(s) }))}
                                        maxHeight="max-h-[200px]"
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {/* Category Filter Dropdown */}
                    {availableCategories.length > 0 && (
                        // Not `relative`: the popover anchors to the filter row's right edge (capped at the
                        // row width) so it can't overflow off-screen from this right-hand button.
                        <div ref={categoryDropdownRef}>
                            <button
                                type="button"
                                onClick={() => {
                                    setCategoryDropdownOpen(!categoryDropdownOpen);
                                    setSupplierDropdownOpen(false);
                                }}
                                className={`flex items-center gap-2 px-3 h-9 rounded-lg border text-[14px] font-medium transition-colors ${
                                    categoryFilters.length > 0
                                        ? 'border-darkGreenColor dark:border-darkGreenColor-dark bg-green-50 dark:bg-green-900/20 text-darkGreenColor dark:text-darkGreenColor-dark'
                                        : 'border-borderGrey dark:border-borderGrey-dark bg-backgroundSecondary dark:bg-backgroundSecondary-dark text-textSecondary dark:text-textSecondary-dark'
                                }`}
                            >
                                <span>Category</span>
                                {categoryFilters.length > 0 && (
                                    <span className="bg-darkGreenColor dark:bg-darkGreenColor-dark text-white dark:text-white text-[12px] px-1.5 rounded-full">
                                        {categoryFilters.length}
                                    </span>
                                )}
                                {categoryDropdownOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </button>
                            {categoryDropdownOpen && (
                                <div className="absolute top-full right-0 -mt-2 z-20 w-[360px] max-w-full bg-white dark:bg-background-dark border border-borderGrey dark:border-borderGrey-dark rounded-lg shadow-lg p-3">
                                    <MultiSelectFilter
                                        label="Categories"
                                        selectedValues={categoryFilters}
                                        onValuesChange={onCategoryFiltersChange}
                                        availableOptions={availableCategories}
                                        valueType="category"
                                        maxHeight="max-h-[280px]"
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {/* Clear Filters */}
                    {hasActiveFilters && (
                        <button
                            type="button"
                            onClick={() => {
                                onSupplierFiltersChange([]);
                                onCategoryFiltersChange([]);
                            }}
                            className="flex items-center gap-1 px-3 h-9 text-[14px] font-medium text-red-500 dark:text-textRed-dark hover:text-red-600 dark:hover:text-textRed-dark transition-colors"
                        >
                            {t("costPrice.automationRun.add.clearFilters")}
                        </button>
                    )}
                </div>
            )}

            {/* Products List */}
            <div
                className={automationReview ? "flex-1 min-h-0 overflow-y-auto" : "flex-1 min-h-0 overflow-y-auto space-y-3"}
                data-onboarding={scopedAnchor(practiceScope, onboardingScope === "subrecipe" ? "cost-sub-add-list" : "cost-add-list")}
            >
            {products.length === 0 ? (
                onCreateCustom && searchQuery.trim() ? null : <div className={`p-4 text-center text-sm ${greyTextColor}`}>
                    {searchQuery ? t("costPrice.automationRun.add.noProductsMatch") : hasActiveFilters
                        ? t("costPrice.automationRun.add.noProductsFiltered") : t("costPrice.automationRun.add.noProducts")}
                </div>
            ) : (
                products.map((product) => {
                    // For platforms without articleId, use name for matching instead
                    const platformLower = product.platform?.toLowerCase() || '';
                    const useNameAsKey = platformsWithoutArticleId.includes(platformLower);
                    const sameSource = selectedProduct?.sourceRestaurantId === product.sourceRestaurantId;
                    const isSelected = sameSource && (useNameAsKey
                        ? selectedProduct?.name === product.name && selectedProduct?.platform === product.platform
                        : selectedProduct?.articleId === product.articleId && selectedProduct?.platform === product.platform);
                    const productKey = useNameAsKey
                        ? `${product.name}-${product.platform}`
                        : `${product.articleId}-${product.platform}`;
                    // Get the appropriate price based on VAT toggle
                    const productPrice = showExVatOrders ? product.price : (product.priceWithTaxes ?? product.price);
                    const unitPrice = productPrice && product.quantityInfo
                        ? calculateUnitPrice(productPrice, product.quantityInfo, product.category)
                        : null;
                    const platformDisplay = getProductPlatformDisplay(product.platform);

                    if (automationReview) return <button
                        key={`${productKey}-${product.sourceRestaurantId ?? "self"}`} type="button"
                        onClick={() => onSelectProduct(product)}
                        className={`flex min-h-[60px] w-full items-center justify-between gap-3 border-b px-4 py-2 text-left last:border-b-0 hover:bg-cardLightGreen dark:hover:bg-cardLightGreen-dark border-borderGrey dark:border-borderGrey-dark ${isSelected ? "bg-cardLightGreen dark:bg-cardLightGreen-dark" : "bg-background dark:bg-background-dark"}`}>
                        <div className="min-w-0">
                            <p className={`break-words text-sm ${primaryText}`}>{product.name}</p>
                            <div className={`mt-0.5 flex flex-wrap items-center gap-2 text-xs ${greyTextColor}`}><span>{formatPlatformName(platformDisplay.displayName)}</span><SourceRestaurantBadge restaurantName={product.sourceRestaurantName} /></div>
                        </div>
                        <div className="shrink-0 text-right">
                            {unitPrice && <span className="rounded-full bg-cardLightGreen px-2 py-1 text-xs font-medium text-darkGreenColor dark:bg-cardLightGreen-dark dark:text-darkGreenColor-dark">{eur(unitPrice.value)}/{unitPrice.unit}</span>}
                            <p className={`mt-1 text-[10px] ${greyTextColor}`}>{t("costPrice.automationRun.add.orderedRecently", { amount: product.orderedLast60 ?? '—' })}</p>
                        </div>
                    </button>;

                    // Guided tour: only the practice fixture's own products are tagged.
                    const practiceKey = practiceScope && isPracticeId(product.articleId)
                        ? product.articleId.slice(PRACTICE_ID_PREFIX.length)
                        : null;
                    const onboardingId = practiceKey
                        ? `${onboardingScope === "subrecipe" ? "cost-sub-add" : "cost-add"}-${practiceKey}`
                        : undefined;

                    return (
                        <button
                            key={`${productKey}-${product.sourceRestaurantId ?? "self"}`}
                            type="button"
                            data-onboarding={onboardingId}
                            onClick={() => onSelectProduct(product)}
                            className={`w-full text-left rounded-lg border py-[11px] pl-3 pr-4 bg-background dark:bg-background-dark flex flex-col transition-colors ${
                                isSelected
                                    ? "border-darkGreenColor dark:border-darkGreenColor-dark"
                                    : borderPrimary
                            }`}
                        >
                            {/* Name (+ estimated price when selected) left, supplier logo pinned
                                top-right taking only the width it needs to render in full. */}
                            <div className="mb-3 flex items-start justify-between gap-3">
                                <div className="flex items-center gap-2 min-w-0 flex-wrap">
                                    <p className={`text-[16px] leading-6 font-semibold tracking-[-0.16px] ${primaryText}`}>
                                        {product.name}
                                    </p>
                                    <SourceRestaurantBadge restaurantName={product.sourceRestaurantName} />
                                    {isSelected && (() => {
                                        const estimatedPrice = calculateEstimatedPrice();
                                        return estimatedPrice !== null ? (
                                            <p className={`text-[16px] leading-6 font-semibold ${darkGreenTextColor}`}>
                                                {eur(estimatedPrice)}
                                            </p>
                                        ) : null;
                                    })()}
                                </div>
                                <div className="flex-shrink-0">
                                    {platformDisplay.showLogo && platformDisplay.platformForLogo ? (
                                        <SupplierLogo
                                            platform={platformDisplay.platformForLogo}
                                            size="medium"
                                            alt={platformDisplay.displayName}
                                        />
                                    ) : (
                                        <span className={`${secondaryText} text-[14px]`}>{platformDisplay.displayName}</span>
                                    )}
                                </div>
                            </div>

                            {/* How much of this product was actually bought recently — the
                                strongest hint about which of several similar products belongs
                                in this recipe. */}
                            <div className="flex items-center justify-between gap-3">
                                <p className={`${greyTextColor} text-[14px] leading-[21px] font-normal tracking-[-0.14px]`}>
                                    {t('costPrice.addIngredient.orderedLast60d')}
                                </p>
                                <p className={`${secondaryText} text-[14px] leading-5 font-medium`}>
                                    {product.orderedLast60 ?? '—'}
                                </p>
                            </div>

                            {unitPrice && (
                                <div className="flex items-center justify-between gap-3 my-2">
                                    <p className={`${greyTextColor} text-[14px] leading-[21px] font-normal tracking-[-0.14px]`}>
                                        {t("costPrice.automationRun.add.unitPrice")}
                                    </p>
                                    <p className={`${secondaryText} text-[14px] leading-5 font-medium`}>
                                        {eur(unitPrice.value)}/{unitPrice.unit}
                                    </p>
                                </div>
                            )}

                            {isSelected && !automationReview && (
                                <div className="mt-3">
                                    <p className={`${secondaryText} mb-3 text-[16px] leading-5 font-medium`}>
                                        {t("costPrice.automationRun.add.amount")}
                                    </p>
                                    <div
                                        className="flex items-center justify-between gap-2 rounded-lg border border-borderGrey dark:border-borderGrey-dark bg-backgroundSecondary dark:bg-backgroundSecondary-dark px-3 h-11"
                                        data-onboarding={scopedAnchor(practiceScope, onboardingScope === "subrecipe" ? "cost-sub-add-qty" : "cost-add-qty")}
                                    >
                                        <input
                                            type="number"
                                            inputMode="decimal"
                                            min={0}
                                            step="any"
                                            placeholder={`0.0 ${product.quantityInfo?.stdUnit || 'units'}`}
                                            value={quantityInputValue}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                if (value === '' || /^[0-9]*\.?[0-9]*$/.test(value)) {
                                                    onQuantityChange(value, value === '' ? 0 : parseFloat(value) || 0);
                                                }
                                            }}
                                            className={`flex-1 bg-transparent dark:bg-transparent text-[14px] leading-5 font-medium ${secondaryText} ${placeholderPrimary} outline-none appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [&::-moz-appearance]:textfield`}
                                        />
                                        <span className={`text-[14px] leading-5 font-medium ${secondaryText}`}>
                                            {product.quantityInfo?.stdUnit || 'units'}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </button>
                    );
                })
            )}
            {truncatedCount > 0 && (
                <div className={`p-3 text-center text-[13px] ${greyTextColor}`}>
                    {t('costPrice.addIngredient.showingFirstResults', { count: products.length, more: truncatedCount })}
                </div>
            )}
            {onCreateCustom && searchQuery.trim() && (
                <button type="button" onClick={() => onCreateCustom(searchQuery.trim())}
                    className={`flex min-h-[44px] w-full items-center gap-2 px-4 py-3 text-left text-sm hover:bg-cardLightGreen dark:hover:bg-cardLightGreen-dark ${primaryText}`}>
                    <Utensils size={16} className="shrink-0" />
                    <span className="flex-1">{t("costPrice.automationRun.add.createNamedCustom", { name: searchQuery.trim() })}</span>
                    <Plus size={16} className="shrink-0" />
                </button>
            )}
            </div>
        </div>
    );
};

export default FromOrdersTab;
